import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */

class QuantitySelectorComponent extends Component {
  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#checkQuantityRules();
    this.updateButtonStates();
  }


  /**
   * Updates the disabled state of minus/plus buttons.
   */
  updateButtonStates() {
    const input = this.refs.quantityInput;
    const buttons = this.refs.quantityButtons;
    if (!input || !buttons) return;

    const current = parseFloat(input.value) || 0;
    const min = parseFloat(input.min) || 0;
    const max = parseFloat(input.max) || Infinity;


    const btns = Array.isArray(buttons) ? buttons : [buttons];

    btns.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (btn.name === 'minus') {
        btn.disabled = current <= min;
      } else if (btn.name === 'plus') {
        btn.disabled = current >= max;
      }
    });
  }

  /**
   * Always false: data-lm is no longer emitted anywhere in the theme, because
   * quantities are whole metres and Shopify rejects a fractional cart quantity.
   * Kept so any input that still carries the attribute is handled predictably.
   */
  #allowsDecimal() {
    const lm = this.refs.quantityInput?.getAttribute('data-lm');
    return lm !== null && lm !== '' && parseFloat(lm) > 0;
  }

  /**
   * Handles the quantity increase event.
   * The +/- buttons always move to the next/previous whole number, even
   * from a decimal LM roll multiple — e.g. 30.4 -> 31, not 30.5 or 31.4.
   * A decimal roll multiple (30.4, 60.8, ...) is only reachable by typing
   * it directly, which is validated separately (see application.js
   * theme.quantityRules).
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const input = this.refs.quantityInput;
    const current = parseFloat(input.value) || 0;
    input.value = String(Math.floor(current) + 1);
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const input = this.refs.quantityInput;
    const min = parseFloat(input.min) || 0;
    const current = parseFloat(input.value) || 0;
    const next = Math.ceil(current) - 1;
    // Never go below min (or below 0 if no min set)
    input.value = String(Math.max(next, min));
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (fires on blur — i.e. the shopper has
   * finished typing and "committed" a value), so this is where the LM
   * roll-multiple check runs.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event, true);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   * @param {boolean} [validateLm] - Whether to run the LM roll-multiple
   *  check. True on blur/typed commit; false for +/- clicks, which always
   *  move by a whole unit from wherever the field currently is (e.g.
   *  30.4 -> 31.4) and are never blocked mid-step.
   */
  #onQuantityChange(event, validateLm = false) {
    const { quantityInput } = this.refs;

    const isValid = this.#checkQuantityRules(validateLm);
    this.updateButtonStates();
    if (!isValid) return;

    const newValue = parseFloat(quantityInput.value);
    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met. When validateLm is true and the value
   * is an invalid LM decimal, shows the round down/up choice prompt instead
   * of accepting the value, and returns false so the caller skips
   * dispatching a cart update for it.
   * @param {boolean} [validateLm]
   */
  #checkQuantityRules = (validateLm = false) => {
    const { quantityInput } = this.refs;
    let numValue = parseFloat(quantityInput.value);
    const numMin = parseFloat(quantityInput.min);
    const numMax = parseFloat(quantityInput.max);

    const container = quantityInput.closest("tr, .inner-grid, .product-form-buttons, .product-information, .quick-add-modal__content, .carts_items");
    const hasMultiplier = container ? container.querySelector(".quntity-note[data-multiplier]") : null;

    if (!isNaN(numValue)) {
      if (this.#allowsDecimal()) {
        numValue = Math.round(numValue * 10) / 10;
        const lmMult = parseFloat(quantityInput.getAttribute('data-lm')) || 0;
        if (validateLm && lmMult > 0 && window.theme?.quantityRules?.evaluateLmQuantity) {
          const lmEval = window.theme.quantityRules.evaluateLmQuantity(numValue, lmMult);
          if (!lmEval.accepted) {
            const lastValid = parseFloat(quantityInput.dataset.lastInput) || parseFloat(quantityInput.defaultValue) || lmMult;
            quantityInput.value = String(lastValid);
            window.theme.quantityRules.promptLmRollChoice(lmMult, lmEval, (chosenValue) => {
              quantityInput.value = String(chosenValue);
              quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(chosenValue, Number(quantityInput.dataset.cartLine)));
            });
            return false;
          }
        }
      } else {
        // Whole-roll LM products (e.g. 50 lm): a typed decimal is never valid, so prompt
        const wholeRoll = parseFloat(quantityInput.getAttribute('data-lm-whole')) || 0;
        if (validateLm && wholeRoll > 1 && numValue !== Math.trunc(numValue) && window.theme?.quantityRules?.evaluateLmQuantity) {
          const lmEval = window.theme.quantityRules.evaluateLmQuantity(numValue, wholeRoll);
          if (!lmEval.accepted) {
            const lastValid = parseFloat(quantityInput.dataset.lastInput) || parseFloat(quantityInput.defaultValue) || wholeRoll;
            quantityInput.value = String(lastValid);
            window.theme.quantityRules.promptLmRollChoice(wholeRoll, lmEval, (chosenValue) => {
              quantityInput.value = String(chosenValue);
              quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(chosenValue, Number(quantityInput.dataset.cartLine)));
            });
            return false;
          }
        }
        // REEL / PK / SHEET etc.: never keep typed decimals like 1.3
        numValue = Math.round(numValue);
        if (quantityInput.step === '0.1' || quantityInput.step === '.1') {
          quantityInput.step = '1';
        }
        quantityInput.removeAttribute('data-lm');
      }
      quantityInput.value = String(numValue);
    }

    if (!isNaN(numMin) && numValue < numMin && !hasMultiplier) quantityInput.value = String(numMin);
    if (!isNaN(numMax) && numValue > numMax) quantityInput.value = String(numMax);
    return true;
  };


  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
